import { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { Box, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { IconPlus } from "@tabler/icons";
import ActionButton from "src/components/shared/ActionButton";
import ResourceList from "src/components/shared/ResourceList";
import { contentApi } from "src/pages/website-content/api";
import { imageUrl, isFile, isOn, matches, placeholderWarnings, rights, shortName } from "src/pages/website-content/checks";
import {
  CheckCell,
  ConfirmDialog,
  ContentPage,
  EditorDialog,
  Field,
  FieldLabel,
  ImageField,
  ImagePreview,
  SaveSwitch,
  ShowFilter,
  useConfirm,
  useContentList,
  useForm,
} from "src/pages/website-content/kit";

// The steps of applying with Safrati, and the reasons visas get refused.
// No page of the current website reads them (STEPS_SHOWN): its "How it
// works" is written in the website code and visa pages have their own steps.
// "Marked for the home page" is the old home page's flag, kept as data.

const KINDS = [
  { value: "success", label: "A step of applying" },
  { value: "reject", label: "A reason visas are refused" },
];
const normal = (text) =>
  String(text || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const EMPTY = { id: null, type: "success", text: "", description: "", order_number: "", image: "" };

// Each section in its own order: by position, then by when it was added.
const byPosition = (a, b) => (Number(a.order_number) || 0) - (Number(b.order_number) || 0) || Number(a.id) - Number(b.id);

// Every row keeps the same 36px slot for its icon, so titles line up
// whether a row has an icon or not.
const IconSlot = ({ src }) =>
  src ? (
    <ImagePreview src={src} width={36} height={36} />
  ) : (
    <Box
      aria-hidden
      title="No icon"
      sx={{ width: 36, height: 36, flexShrink: 0, borderRadius: "10px", border: "1px dashed", borderColor: "divider" }}
    />
  );

const WorkingProcess = () => {
  const dispatch = useDispatch();
  const can = rights("16");
  const load = useCallback(() => contentApi.workingProcess.list(dispatch), [dispatch]);
  const list = useContentList(load);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("all");
  const [editing, setEditing] = useState(null);
  const form = useForm(EMPTY);
  const confirm = useConfirm();

  // The API's order is the order the home page uses.
  const items = useMemo(() => list.items || [], [list.items]);
  const isRefusal = (i) => i.type === "reject";
  // Steps and refusal reasons are two lists, each numbered from 1: in one
  // list "Position 1" appeared three times.
  const FILTERS = {
    all: () => true,
    home: (i) => isOn(i.is_main),
    inactive: (i) => !isOn(i.is_active),
  };
  const shown = items.filter(FILTERS[tab]).filter((i) => matches(i, search, ["text", "description"]));
  const shownSteps = shown.filter((i) => !isRefusal(i)).sort(byPosition);
  const shownRefusals = shown.filter(isRefusal).sort(byPosition);
  const narrowed = tab !== "all" || search.trim() !== "";
  const count = (key) => items.filter(FILTERS[key]).length;

  // A description copied word for word from another step reads as a mistake.
  const sameText = useMemo(() => {
    const byText = {};
    items.forEach((i) => {
      const key = normal(i.description);
      if (key) byText[key] = [...(byText[key] || []), i];
    });
    return byText;
  }, [items]);
  const warningsFor = (item) => {
    const found = placeholderWarnings(item.text, item.description);
    const others = (sameText[normal(item.description)] || []).filter((i) => i.id !== item.id);
    if (others.length)
      found.push(`Same text as ${others.map((i) => (i.text === item.text ? `another “${i.text}” step` : `“${i.text}”`)).join(", ")}.`);
    return found;
  };

  const set = async (item, field, next, message) => {
    try {
      await contentApi.workingProcess.set(item.id, field, next ? 1 : 0, dispatch);
      list.patch(item.id, { [field]: next ? 1 : 0 });
      toast.success(message);
    } catch {
      /* PostFunction said what went wrong; the switch stays */
    }
  };

  const openEditor = (item, type = "success") => {
    const sameKind = items.filter((i) => (i.type || "success") === type);
    form.reset(
      item
        ? {
            id: item.id,
            type: item.type || "success",
            text: item.text || "",
            description: item.description || "",
            order_number: item.order_number ?? "",
            image: item.image || "",
          }
        : { ...EMPTY, type, order_number: String(sameKind.length ? Math.max(...sameKind.map((i) => Number(i.order_number) || 0)) + 1 : 1) }
    );
    setEditing(item ? { item } : { item: null });
  };

  const save = async () => {
    const v = form.values;
    const errors = {};
    if (!v.text.trim()) errors.text = "Give the step a short title.";
    if (!v.description.trim()) errors.description = "Explain the step in a sentence.";
    if (String(v.order_number).trim() && !/^\d{1,3}$/.test(String(v.order_number).trim())) errors.order_number = "Use a whole number, like 1 or 2.";
    form.setErrors(errors);
    if (Object.keys(errors).length) return false;
    const before = editing.item;
    const body = {
      type: v.type,
      text: v.text.trim(),
      description: v.description.trim(),
      order_number: String(v.order_number).trim() || "1",
    };
    // The image is sent only when it changed: a new file, or "" to remove it.
    if (isFile(v.image)) body.image = v.image;
    else if (before && before.image && !v.image) body.image = "";
    try {
      if (before) {
        await contentApi.workingProcess.edit({ id: v.id, ...body }, dispatch);
        toast.success("Step saved.");
      } else {
        await contentApi.workingProcess.add(body, dispatch);
        toast.success("Step added.");
      }
      setEditing(null);
      list.reload();
      return true;
    } catch {
      return undefined;
    }
  };

  // The same row in both lists: icon slot, title and explanation.
  const primary = (item) => ({
    title: item.text,
    subtitle: shortName(item.description, 90),
    avatar: <IconSlot src={item.image ? imageUrl(item.image) : null} />,
  });
  const columns = [
    { key: "position", label: "Position", width: 90, hideBelow: "lg", render: (item) => item.order_number ?? "—" },
    {
      key: "active",
      label: "Switched on",
      width: 170,
      render: (item) => (
        <SaveSwitch
          label={isOn(item.is_active) ? "Switched on" : "Switched off"}
          item={item.text}
          checked={isOn(item.is_active)}
          disabled={!can.edit}
          onChange={(next) => set(item, "is_active", next, `“${item.text}” is switched ${next ? "on" : "off"}.`)}
        />
      ),
    },
    {
      key: "check",
      label: "Needs a look",
      hideBelow: "md",
      render: (item) => <CheckCell warnings={warningsFor(item)} />,
    },
  ];
  const rowActions = (item) => [
    can.edit && { label: "Edit", onClick: () => openEditor(item) },
    can.edit && {
      label: isOn(item.is_main) ? "Unmark for the home page" : "Mark for the home page",
      onClick: () =>
        set(item, "is_main", !isOn(item.is_main), isOn(item.is_main) ? "Step unmarked for the home page." : "Step marked for the home page."),
    },
    can.delete && { label: "Delete", destructive: true, onClick: () => confirm.ask(item) },
  ];

  return (
    <ContentPage
      title="How it works"
      intro="The steps of applying with Safrati, and common reasons visas are refused."
      where="The website doesn’t show these steps yet: its “How it works” is written in the website code, and each visa page has its own steps."
      canAdd={can.write}
      addLabel="Add a step"
      onAdd={() => openEditor(null)}
      dialogs={
        <>
          <EditorDialog
            open={!!editing}
            title={editing?.item ? "Edit step" : "Add a step"}
            subtitle="No page of the current website shows these steps. Your changes are kept for when one does."
            onClose={() => setEditing(null)}
            onSave={save}
            saveLabel={editing?.item ? "Save step" : "Add step"}
            errors={form.errors}
            dirty={form.dirty}
          >
            <Box>
              <FieldLabel id="step-kind">Kind</FieldLabel>
              <TextField
                select
                fullWidth
                value={form.values.type}
                onChange={(e) => form.set("type", e.target.value)}
                SelectProps={{ labelId: "step-kind" }}
                sx={{ "& .MuiInputBase-root": { minHeight: 48, borderRadius: "14px" } }}
              >
                {KINDS.map((k) => (
                  <MenuItem key={k.value} value={k.value}>
                    {k.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <Field
              label="Title"
              value={form.values.text}
              onChange={(v) => form.set("text", v)}
              error={form.errors.text}
              hint="A few words."
              counter={40}
            />
            <Field
              label="Explanation"
              value={form.values.description}
              onChange={(v) => form.set("description", v)}
              error={form.errors.description}
              hint="One sentence about what happens."
              multiline
              rows={2}
            />
            <Field
              label="Position"
              optional
              value={form.values.order_number}
              onChange={(v) => form.set("order_number", v)}
              error={form.errors.order_number}
              hint="1 is first."
              inputProps={{ inputMode: "numeric" }}
            />
            <ImageField
              label="Icon"
              optional
              value={form.values.image}
              onChange={(v) => form.set("image", v)}
              hint="SVG or PNG, shown at 48 × 48 px."
              preview={(src) => <ImagePreview src={src} width={48} height={48} />}
            />
            {placeholderWarnings(form.values.text, form.values.description).length > 0 && (
              <Typography variant="body2" color="warning.dark" role="status">
                {placeholderWarnings(form.values.text, form.values.description).join(" ")}
              </Typography>
            )}
          </EditorDialog>

          <ConfirmDialog
            open={confirm.open}
            title={confirm.item && isRefusal(confirm.item) ? "Delete this refusal reason?" : "Delete this step?"}
            confirmLabel="Delete"
            cancelLabel="Cancel"
            onClose={confirm.close}
            onConfirm={async () => {
              try {
                await contentApi.workingProcess.remove(confirm.item.id, dispatch);
                toast.success(`“${confirm.item?.text || "The step"}” deleted.`);
                list.reload();
                return true;
              } catch {
                return false;
              }
            }}
          >
            “{confirm.item?.text}” is deleted, also from any visa it is attached to in Countries. You can restore it from the Activity log. To
            keep it, switch it off instead.
          </ConfirmDialog>
        </>
      }
    >
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="h5" component="h2">
          Steps
        </Typography>
        <Typography variant="body2" color="text.secondary">
          The steps of applying with Safrati, in order.
        </Typography>
      </Box>
      <ResourceList
        label="Steps"
        items={shownSteps}
        primaryLabel="Step"
        primary={primary}
        columns={columns}
        onOpen={can.edit ? openEditor : undefined}
        openLabel={(item) => `Edit the step “${item.text}”`}
        rowActions={rowActions}
        // Always a table, like the list below it, so the two line up.
        sparse={0}
        search={{ value: search, onChange: setSearch, placeholder: "Search steps and reasons" }}
        filters={
          <ShowFilter
            value={tab}
            onChange={setTab}
            options={[
              { value: "all", label: "All", count: count("all") },
              { value: "home", label: "Marked for the home page", count: count("home") },
              { value: "inactive", label: "Switched off", count: count("inactive") },
            ]}
          />
        }
        loading={list.loading}
        error={list.failed}
        onRetry={list.reload}
        empty={{ title: narrowed ? "No step matches." : "No steps yet." }}
      />

      {/* The refusal reasons, under their own heading. Hidden while the
          list failed to load: the error above covers both. */}
      {!list.failed && (
        <Box component="section" aria-labelledby="refusals-title" sx={{ mt: 4 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 1.5 }}>
            <Box>
              <Typography id="refusals-title" variant="h5" component="h2">
                Common refusal reasons
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Why visas get refused, in the order you set.
              </Typography>
            </Box>
            {can.write && (
              <ActionButton startIcon={<IconPlus size={18} />} onClick={() => openEditor(null, "reject")}>
                Add a reason
              </ActionButton>
            )}
          </Stack>
          <ResourceList
            label="Common refusal reasons"
            items={shownRefusals}
            primaryLabel="Reason"
            primary={primary}
            columns={columns}
            onOpen={can.edit ? openEditor : undefined}
            openLabel={(item) => `Edit the refusal reason “${item.text}”`}
            rowActions={rowActions}
            sparse={0}
            loading={list.loading}
            empty={{ title: narrowed ? "No refusal reason matches." : "No refusal reasons yet." }}
          />
        </Box>
      )}
    </ContentPage>
  );
};

export default WorkingProcess;
